import {noop} from '@benchling/js-utils';
import {css} from 'aphrodite';
import React, {useState} from 'react';

import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import getTestProps from 'test/lib/cypress/getTestProps';

import {FlowchartConnectionMode} from '../../flowcharts/design/procedureRunDesignFlowchartConnectionUtils';
import ProcedureRunDesignFlowchartEditor from '../../flowcharts/design/ProcedureRunDesignFlowchartEditor';
import ProcedureSearchSidepanel, {
  PROCEDURE_SELECTION_DND_MIME_TYPE,
  SelectedProcedure,
} from '../procedure-selection/ProcedureSearchSidepanel';
import {studyDesignTabStyles} from './StudyDesignTabSharedComponents';

// Define a single empty object to avoid unnecessary re-renders.
const EMPTY_EXPERIMENTAL_INPUTS: ExperimentalInputs = {};

/**
 * Renders the editor for a procedure run plan that has no linked flowchart version yet
 * (procedure-less). Reuses the real flowchart editor shell with empty methods/conditions so the
 * empty state matches the populated editor (same sidebar, panels, and canvas background). Dragging
 * a procedure card from the search sidepanel onto the empty canvas attaches its flowchart version
 * to the existing plan and refetches.
 *
 */
export default function EmptyProcedureRunPlanDesignTabContents({
  studyType,
  procedureType,
  onAttachProcedure,
}: {
  studyType: StudyType;
  procedureType: ProcedureType;
  onAttachProcedure: (selectedProcedure: SelectedProcedure) => void;
}): JSX.Element {
  const [isDropTargetActive, setIsDropTargetActive] = useState(false);

  function parseDroppedProcedure(event: React.DragEvent<HTMLDivElement>): SelectedProcedure | null {
    const dndPayload = event.dataTransfer.getData(PROCEDURE_SELECTION_DND_MIME_TYPE);
    if (dndPayload.length === 0) {
      return null;
    }
    let parsed: Partial<SelectedProcedure>;
    try {
      parsed = JSON.parse(dndPayload) as Partial<SelectedProcedure>;
    } catch (err) {
      // Return null if the user tries to drop something without a valid JSON payload
      if (err instanceof SyntaxError) {
        return null;
      }
      throw err;
    }
    // Return null if the user tries to drop something other than the procedure card
    if (
      typeof parsed.procedureId !== 'string' ||
      typeof parsed.workflowFlowchartConfigVersionId !== 'string'
    ) {
      return null;
    }
    return {
      procedureId: parsed.procedureId,
      workflowFlowchartConfigVersionId: parsed.workflowFlowchartConfigVersionId,
    };
  }

  return (
    <div
      className={css(studyDesignTabStyles.designBackground)}
      {...getTestProps({component: 'EmptyProcedureRunPlanDesignTabContents', element: 'root'})}
    >
      <ProcedureRunDesignFlowchartEditor
        conditions={[]}
        methods={[]}
        activeMethodId={null}
        procedureType={procedureType}
        connectionOptions={{connectionMode: FlowchartConnectionMode.DISABLED}}
        experimentalInputs={EMPTY_EXPERIMENTAL_INPUTS}
        selectedMethodId={null}
        onChangeSelectedMethod={noop}
        // Force the left panel open so the procedure search sidepanel is visible immediately.
        isInEditState={true}
        allowEditingPersistedConditions={true}
        edgeSpecs={[]}
        leftPanelViews={[
          {
            icon: 'create',
            tooltip: 'Run Plan',
            name: 'Run Plan',
            content: <ProcedureSearchSidepanel procedureType={procedureType} studyType={studyType} />,
          },
        ]}
        rightPanelChildren={null}
        canvasDnd={{
          isDropTargetActive,
          onDragEnter: (event) => {
            event.preventDefault();
            if (!event.dataTransfer.types.includes(PROCEDURE_SELECTION_DND_MIME_TYPE)) {
              return;
            }
            setIsDropTargetActive(true);
          },
          onDragOver: (event) => {
            event.preventDefault();
            if (event.dataTransfer.types.includes(PROCEDURE_SELECTION_DND_MIME_TYPE)) {
              event.dataTransfer.dropEffect = 'copy';
            }
          },
          onDragLeave: (event) => {
            event.preventDefault();
            setIsDropTargetActive(false);
          },
          onDrop: (event) => {
            event.preventDefault();
            setIsDropTargetActive(false);
            const selectedProcedure = parseDroppedProcedure(event);
            if (selectedProcedure == null) {
              return;
            }
            onAttachProcedure(selectedProcedure);
          },
        }}
      />
    </div>
  );
}
